import type {
  CatalogueBottomEndFormValues,
  CatalogueCoilFrameFormValues,
  CataloguePowerSourceFormValues,
} from "../../../web-app";

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function isPositiveFiniteNumber(value: string): boolean {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
}

export function defaultPowerSourceValues(rowNumber: number): CataloguePowerSourceFormValues {
  return {
    id: "",
    name: "",
    powerSourceRatingKva: "",
    powerSourceFrequencyHz: "",
    maximumPowerSourceVoltageV: "",
    maximumPowerSourceCurrentA: "",
    minimumPowerUtilization: "0.55",
    maximumPowerUtilization: "0.85",
  };
}

export function defaultCoilFrameValues(_rowNumber: number): CatalogueCoilFrameFormValues {
  return {
    id: "",
    name: "",
    coilFrameSizeInches: "",
    coilFrameInsideDiameterMm: "",
  };
}

export function defaultBottomEndValues(_rowNumber: number): CatalogueBottomEndFormValues {
  return {
    id: "",
    name: "",
    numberOfTurns: "",
    firstContactEdgeOffsetMm: "52.5",
    lastContactEdgeOffsetMm: "52.5",
    contactCenterSpacingsMm: "",
    bottomEndWidthMm: "",
    heatingResponseCelsiusMeterPerKwMinute: "0.35",
  };
}

export function buildPowerSourceFormErrors(
  values: CataloguePowerSourceFormValues,
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (values.id.trim() === "") errors.id = "ID is required.";
  if (values.name.trim() === "") errors.name = "Name is required.";
  if (!isPositiveFiniteNumber(values.powerSourceRatingKva)) {
    errors.powerSourceRatingKva = "Rating must be a positive number.";
  }
  if (!isPositiveFiniteNumber(values.powerSourceFrequencyHz)) {
    errors.powerSourceFrequencyHz = "Frequency must be a positive number.";
  }
  if (!isPositiveFiniteNumber(values.maximumPowerSourceVoltageV)) {
    errors.maximumPowerSourceVoltageV = "Max Voltage must be a positive number.";
  }
  if (!isPositiveFiniteNumber(values.maximumPowerSourceCurrentA)) {
    errors.maximumPowerSourceCurrentA = "Max Current must be a positive number.";
  }
  return errors;
}

export function buildCoilFrameFormErrors(
  values: CatalogueCoilFrameFormValues,
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (values.id.trim() === "") errors.id = "ID is required.";
  if (values.name.trim() === "") errors.name = "Name is required.";
  if (!isPositiveFiniteNumber(values.coilFrameSizeInches)) {
    errors.coilFrameSizeInches = "Frame Size must be a positive number.";
  }
  if (!isPositiveFiniteNumber(values.coilFrameInsideDiameterMm)) {
    errors.coilFrameInsideDiameterMm = "Inside Diameter must be a positive number.";
  }
  return errors;
}

export function buildBottomEndFormErrors(
  values: CatalogueBottomEndFormValues,
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (values.name.trim() === "") errors.name = "Name is required.";

  let turns = 0;
  if (
    !isPositiveFiniteNumber(values.numberOfTurns) ||
    !Number.isInteger(Number(values.numberOfTurns))
  ) {
    errors.numberOfTurns = "Turns must be a positive whole number.";
  } else {
    turns = Number(values.numberOfTurns);
  }

  if (!isPositiveFiniteNumber(values.bottomEndWidthMm)) {
    errors.bottomEndWidthMm = "Bottom End Width must be a positive number.";
  }

  const spacingValues = values.contactCenterSpacingsMm
    .split(",")
    .map((value) => value.trim())
    .filter((value) => value !== "");

  if (spacingValues.length === 0) {
    errors.contactCenterSpacingsMm = "Contact Spacing values must be provided.";
  } else if (turns > 0 && spacingValues.length !== turns - 1) {
    errors.contactCenterSpacingsMm = `Expected ${turns - 1} value${turns - 1 === 1 ? "" : "s"} for ${turns} turns.`;
  } else if (spacingValues.some((value) => !isPositiveFiniteNumber(value))) {
    errors.contactCenterSpacingsMm = "All Contact Spacing values must be positive numbers.";
  }

  return errors;
}

export function normalizePowerSourceValues(
  values: CataloguePowerSourceFormValues,
  existing?: CataloguePowerSourceFormValues,
): CataloguePowerSourceFormValues {
  return {
    id: values.id.trim(),
    name: values.name.trim(),
    powerSourceRatingKva: values.powerSourceRatingKva.trim(),
    powerSourceFrequencyHz: values.powerSourceFrequencyHz.trim(),
    maximumPowerSourceVoltageV: values.maximumPowerSourceVoltageV.trim(),
    maximumPowerSourceCurrentA: values.maximumPowerSourceCurrentA.trim(),
    minimumPowerUtilization: existing?.minimumPowerUtilization ?? "0.55",
    maximumPowerUtilization: existing?.maximumPowerUtilization ?? "0.85",
  };
}

export function normalizeCoilFrameValues(
  values: CatalogueCoilFrameFormValues,
): CatalogueCoilFrameFormValues {
  return {
    id: values.id.trim(),
    name: values.name.trim(),
    coilFrameSizeInches: values.coilFrameSizeInches.trim(),
    coilFrameInsideDiameterMm: values.coilFrameInsideDiameterMm.trim(),
  };
}

export function normalizeBottomEndValues(
  values: CatalogueBottomEndFormValues,
  existing?: CatalogueBottomEndFormValues,
): CatalogueBottomEndFormValues {
  const name = values.name.trim();
  return {
    id: existing?.id ?? (slugify(name) || `bottom-end-${Date.now()}`),
    name,
    numberOfTurns: values.numberOfTurns.trim(),
    firstContactEdgeOffsetMm: existing?.firstContactEdgeOffsetMm ?? "52.5",
    lastContactEdgeOffsetMm: existing?.lastContactEdgeOffsetMm ?? "52.5",
    contactCenterSpacingsMm: values.contactCenterSpacingsMm.trim(),
    bottomEndWidthMm: values.bottomEndWidthMm.trim(),
    heatingResponseCelsiusMeterPerKwMinute:
      existing?.heatingResponseCelsiusMeterPerKwMinute ?? "0.35",
  };
}

export function formatBottomEndCoilSection(row: CatalogueBottomEndFormValues): string {
  const width = Number(row.bottomEndWidthMm);
  const first = Number(row.firstContactEdgeOffsetMm);
  const last = Number(row.lastContactEdgeOffsetMm);
  if (!Number.isFinite(width) || !Number.isFinite(first) || !Number.isFinite(last)) {
    return "—";
  }
  const section = width - first - last;
  if (!Number.isFinite(section) || section <= 0) return "—";
  return `${section.toFixed(1)} mm`;
}
